import type { ResolvedTheme, SoundDef, SoundEvent } from '../types';
import { playSynth } from './synth';

export interface SoundHandle {
  stop(): void;
}

const NOOP: SoundHandle = { stop() {} };

/**
 * Plays theme sounds: audio files when a theme provides them, synthesized presets otherwise.
 * The AudioContext is created lazily on unlock() (first user gesture) — browsers require it.
 */
export class AudioManager {
  private manifest: Record<SoundEvent, SoundDef>;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private on = true;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private active = new Map<SoundEvent, Set<SoundHandle>>();
  private destroyed = false;

  constructor(theme: ResolvedTheme) {
    this.manifest = theme.sounds;
    try {
      this.on = globalThis.localStorage?.getItem('simple-slot:sound') !== '0';
    } catch {
      /* storage blocked */
    }
  }

  /** Call from a user gesture. Safe to call repeatedly. */
  unlock(): void {
    if (this.destroyed) return;
    const Ctor = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
      (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    if (!this.ctx) {
      try {
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.on ? 1 : 0;
        this.master.connect(this.ctx.destination);
      } catch {
        this.ctx = null;
        return;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
  }

  get enabled(): boolean {
    return this.on;
  }

  setEnabled(on: boolean): void {
    this.on = on;
    try {
      globalThis.localStorage?.setItem('simple-slot:sound', on ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (this.master && this.ctx) {
      const now = this.ctx.currentTime;
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.linearRampToValueAtTime(on ? 1 : 0, now + 0.05);
    }
    if (!on) this.stopAll();
  }

  play(event: SoundEvent): SoundHandle {
    const def = this.manifest[event];
    if (!def || !this.on || !this.ctx || !this.master || this.destroyed) return NOOP;
    if (this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {});
    const volume = def.volume ?? 1;
    const loop = !!def.loop;
    let handle: SoundHandle;
    if (def.src) {
      const list = Array.isArray(def.src) ? def.src : [def.src];
      const url = list[Math.floor(Math.random() * list.length)];
      handle = this.playFile(url, volume, loop, def);
    } else {
      handle = playSynth(this.ctx, this.master, def.synth ?? 'none', { volume, loop });
    }
    const set = this.active.get(event) ?? new Set();
    this.active.set(event, set);
    const tracked: SoundHandle = {
      stop: () => {
        set.delete(tracked);
        handle.stop();
      },
    };
    set.add(tracked);
    if (!loop) setTimeout(() => set.delete(tracked), 6000);
    return tracked;
  }

  private playFile(url: string, volume: number, loop: boolean, def: SoundDef): SoundHandle {
    const ctx = this.ctx!;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    gain.connect(this.master!);
    let source: AudioBufferSourceNode | null = null;
    let stopped = false;
    let fallback: SoundHandle | null = null;
    void this.buffer(url).then((buf) => {
      if (stopped || this.destroyed) return;
      if (!buf) {
        // file missing/undecodable: fall back to the synth preset if any
        if (def.synth) fallback = playSynth(ctx, this.master!, def.synth, { volume, loop });
        return;
      }
      source = ctx.createBufferSource();
      source.buffer = buf;
      source.loop = loop;
      source.connect(gain);
      source.start();
    });
    return {
      stop: () => {
        if (stopped) return;
        stopped = true;
        fallback?.stop();
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(gain.gain.value, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.05);
        setTimeout(() => {
          try {
            source?.stop();
          } catch {
            /* already ended */
          }
          source?.disconnect();
          gain.disconnect();
        }, 80);
      },
    };
  }

  private buffer(url: string): Promise<AudioBuffer | null> {
    let p = this.buffers.get(url);
    if (!p) {
      p = fetch(url)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((ab) => this.ctx!.decodeAudioData(ab))
        .catch(() => {
          console.warn(`[simple-slot] sound not loaded: ${url}`);
          return null;
        });
      this.buffers.set(url, p);
    }
    return p;
  }

  stop(event: SoundEvent): void {
    const set = this.active.get(event);
    if (!set) return;
    for (const h of [...set]) h.stop();
    set.clear();
  }

  stopAll(): void {
    for (const ev of [...this.active.keys()]) this.stop(ev);
  }

  setTheme(theme: ResolvedTheme): void {
    this.stopAll();
    this.manifest = theme.sounds;
  }

  destroy(): void {
    this.stopAll();
    this.destroyed = true;
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.master = null;
  }
}
